import React, { useEffect, useMemo, useState } from "react";
import storyApiPaths from "../../config/apiPaths";
import { storyAssets } from "../../constants/assets";
import { useRetrieveSelQuestions } from "../../useStoryOperations";
import WebSnowfall from "../WebSnowfall";
import WebLottiePlayer from "../WebLottiePlayer";
import CustomChatBubble from "../CustomChatBubble";
import WebShadowButton from "../WebShadowButton";
import WebAudioPlayer from "../WebAudioPlayer";
import WebBreakModal from "./WebBreakModal";
import WebPledgeCard from "./WebPledgeCard";
import WebSelQuestionsModal, { SelQuestionsData } from "./WebSelQuestionsModal";
import WebClosingConvoScreen from "./WebClosingConvoScreen";
import WebIntegratedLessonPlan, {
  LessonPlanQuestion,
} from "../WebIntegratedLessonPlan";
import "./WebStoryBreakScreen.css";
import { TrackingEvents } from "../../../../Utilities/analytics";
import { trackEvent } from "../../../../../mixpanel";
import { useNavigate, useLocation } from "react-router-dom";
import { yetiPreview } from "../../../../appAssets";
import ChatBubble from "../../../../components/ChatBubble/ChatBubble";
import { WEB_SCREENS, WebStoryNavigation } from "../../exports";
import { createWebStoryNavigation } from "../../navigation/createWebStoryNavigation";

const ACTIVITY_CONVERSATION = {
  content: "Now, lets do a fun activity! I have something special in mind!",
  audio:
    "https://assets.dev.yetimyfriend.com/Troo_Now_lets_do_a_fun_activity_I_have_something_special_in_mind_cf1e34d1e3.mp3",
  characterImage: "https://assets.dev.yetimyfriend.com/yeti_310ac5ef13.json",
  characterFontColor: "Default:#000000",
};

const STORY_ACTIVITY_CONVERSATION = {
  content: `Wait! I have a question for you. I've been thinking...`,
  audio:
    "https://assets.prod.yetimyfriend.com/troo_wait_i_have_a_question_for_you_i_ve_been_thinking_f28962eed7.mp3",
  characterImage: "https://assets.dev.yetimyfriend.com/yeti_310ac5ef13.json",
  characterFontColor: "Default:#000000",
};

const STORY_BREAK_PRE_RENDER_CONVERSATION = {
  content: `Oooh, I hear a buzz… looks like there’s something from the magic backpack…`,
  audio:
    "https://assets.prod.yetimyfriend.com/troo_oooh_i_hear_a_buzz_looks_like_there_s_something_from_the_magic_backpack_aca17c4a9e.mp3",
  characterImage: "https://assets.dev.yetimyfriend.com/yeti_310ac5ef13.json",
  characterFontColor: "Default:#000000",
};

interface PledgeConversation {
  id?: number;
  content?: string;
  audio?: string;
  characterImage?: string;
  characterFontColor?: string;
}

interface ClosingConversation {
  bgImage?: string;
  conversations?: PledgeConversation[];
  userActions?: Array<{ id: number; buttonText: string; linkTo: string }>;
}

interface BreakButton {
  btnIcon: string;
  btnTitle: string;
  btnAudio: string;
  btnDuration: string;
  btnLinkTo: string;
}

export interface StoryBreakMilestoneData {
  type: string;
  subType: string;
  chapter?: number;
  image?: string;
  content?: string;
  audio?: string;
  contentAudio?: string;
  linkTo?: string;
  takeABreak?: {
    title: string;
    audio: string;
    buttons: BreakButton[];
  };
  pledge?: {
    bgImage?: string;
    conversations?: PledgeConversation[];
    userActions?: unknown[];
  };
  closingConversation?: ClosingConversation;
  [key: string]: unknown;
}

export interface WebStoryBreakScreenProps {
  onTrackEvent?: (event: string, props?: Record<string, unknown>) => void;
  contentBaseUrl?: string;
}

const getExtension = (url?: string): string => {
  if (!url) return "";
  const parts = url.split(".");
  return parts[parts.length - 1].toLowerCase().split("?")[0];
};

const WebStoryBreakScreen: React.FC<WebStoryBreakScreenProps> = ({
  onTrackEvent,
  contentBaseUrl,
}) => {
  // const navigate = useNavigate<WebStoryNavigation>();
  const routerNavigate = useNavigate();
  const navigation = useMemo(
    () => createWebStoryNavigation(routerNavigate),
    [routerNavigate],
  );
  const location = useLocation();
  const track = onTrackEvent ?? trackEvent;

  // Read all data directly from navigation state
  const routeState = (location.state ?? {}) as {
    milestoneData?: StoryBreakMilestoneData | undefined;
    bgImage?: string;
    documentId?: string;
    storyId?: string;
    lessonPlanQuestion?: LessonPlanQuestion;
  };

  const milestoneData = routeState.milestoneData;
  const bgImage = routeState.bgImage;
  const documentId = routeState.documentId;
  const storyId = routeState.storyId;
  const lessonPlanQuestionFromState = routeState.lessonPlanQuestion;

  console.log(milestoneData, routeState.documentId, "dsfksjdfshbjfgbsjdfgsfd");

  const [showLessonPlan, setShowLessonPlan] = useState(
    !!lessonPlanQuestionFromState,
  );
  const [showBreakModal, setShowBreakModal] = useState(false);
  const [audioUrl, setAudioUrl] = useState("");
  const [conversationIndex, setConversationIndex] = useState(0);
  const [qstnStep, setQstnStep] = useState(0);
  const [qstnData, setQstnData] = useState<SelQuestionsData | null>(null);
  const [showSelQuestionsModal, setShowSelQuestionsModal] = useState(false);
  const [showClosingConvo, setShowClosingConvo] = useState(false);
  const [preAudioDone, setPreAudioDone] = useState(false);
  const { retrieveSelQuestions } = useRetrieveSelQuestions();

  // Redirect if no milestone data in state
  useEffect(() => {
    if (!milestoneData) {
      navigation.navigate(WEB_SCREENS.AppJourneyPage, { replace: true });
    }
  }, [milestoneData, navigation]);

  const isMilestoneActivity =
    milestoneData?.type?.toLowerCase() === "question" &&
    milestoneData?.chapter === 2;

  useEffect(() => {
    setAudioUrl(milestoneData?.audio ?? "");
    if (documentId) {
      retrieveSelQuestions(documentId)
        .then((res: { data?: SelQuestionsData }) =>
          setQstnData(res?.data ?? null),
        )
        .catch(() => undefined);
    }
  }, [milestoneData?.audio, documentId, retrieveSelQuestions]);

  const continueToStory = () => {
    console.log(isMilestoneActivity, milestoneData?.subType?.toLowerCase());
    if (
      isMilestoneActivity &&
      milestoneData?.subType?.toLowerCase() === "story"
    ) {
      console.log(
        "Navigating to story break screen with contentID:",
        milestoneData.linkTo,
      );
      // navigation.navigate("/web/WebStoryScreen", {
      // navigation.navigate(WEB_SCREENS.WebStoryScreen, {
      //   state: {
      //     navigatedFrom: "storyBreakScreen",
      //     isChatScreen: false,
      //     contentID: milestoneData.linkTo,
      //   },
      // });

      navigation.navigate(WEB_SCREENS.ApplicationNavigatorStack, {
        screen: WEB_SCREENS.ChatBoxScreen,
        params: {
          navigatedFrom: "storyBreakScreen",
          isChatScreen: false,
          contentID: milestoneData.linkTo,
          storyId: storyId,
        },
      });
      return;
    }

    routerNavigate(-1);
  };

  const onConfirmBreakAction = (selectedBtnLinkTo: string) => {
    setShowBreakModal(false);
    if (selectedBtnLinkTo === "AdventureScreen") {
      continueToStory();
    }

    if (selectedBtnLinkTo.toLowerCase() === "funboxscreen") {
      if (milestoneData?.subType.toLowerCase() === "chat") {
        // navigate("/web/appJourneyPage", { state: { storyId } });

        navigation.navigate(WEB_SCREENS.ApplicationNavigatorStack, {
          screen: WEB_SCREENS.ChatBoxScreen,
          params: {
            navigatedFrom: "storyBreakScreen",
            contentID: milestoneData.linkTo,
            isChatScreen: true,
          },
        });
        return;
      } else if (milestoneData?.subType.toLowerCase() === "story") {
        navigation.navigate(WEB_SCREENS.ApplicationNavigatorStack, {
          screen: WEB_SCREENS.ChatBoxScreen,
          params: {
            navigatedFrom: "storyBreakScreen",
            contentID: milestoneData.linkTo,
            isChatScreen: false,
          },
        });
        // navigation.navigate(SCREENS.ApplicationNavigatorStack, {
        //   screen: SCREENS.ChatBoxScreen,
        //   params: {
        //     navigatedFrom: "storyBreakScreen",
        //     isChatScreen: false,
        //     contentID: milestoneData.linkTo,
        //   },
        // });
      } else
        navigation.navigate(WEB_SCREENS.ApplicationNavigatorStack, {
          screen: WEB_SCREENS.FunBoxScreen,
          params: {
            navigatedFrom: "storyBreakScreen",
          },
        });
      // navigate("/web/funBoxScreen", {
      //   state: {
      //     navigatedFrom: "storyBreakScreen",
      //   },
      // });
    }
    if (selectedBtnLinkTo === "HomeScreen") {
      navigation.navigate(WEB_SCREENS.HomeStack, { replace: true });
      // navigation.dispatch(
      //   CommonActions.reset({
      //     index: 0,
      //     routes: [{name: SCREENS.HomeStack}],
      //   }),
      // );
    }
  };

  const renderConversation = (
    data: PledgeConversation[] | undefined,
    index: number,
  ) => {
    const item = data?.[index];
    if (!item?.content?.trim()) return null;
    return (
      <ChatBubble
        key={index}
        tailSide={"left"}
        content={item.content ?? ""}
        textColor={item.characterFontColor ?? ""}
        audioUri={item.audio ?? ""}
      />
    );
  };

  const renderActivityConversation = (item: {
    content?: string;
    audio?: string;
    characterFontColor?: string;
  }) => {
    if (!item?.content?.trim()) return null;

    return (
      <ChatBubble
        tailSide={"left"}
        content={item.content ?? ""}
        textColor={item.characterFontColor ?? ""}
        audioUri={item.audio ?? ""}
      />
    );
  };

  if (!milestoneData) {
    return null;
  }

  if (showClosingConvo && milestoneData.closingConversation) {
    const closingConvo = milestoneData.closingConversation;
    return (
      <WebClosingConvoScreen
        bgImage={closingConvo.bgImage ?? bgImage}
        conversations={closingConvo.conversations ?? []}
        onTrackEvent={track}
        contentBaseUrl={contentBaseUrl}
      />
    );
  }

  // ── Pledge mode ───────────────────────────────────────────────────────────
  if (milestoneData.type?.toLowerCase() === "pledge") {
    const pledgeConversations = milestoneData.pledge?.conversations ?? [];
    console.log(
      milestoneData?.closingConversation?.bgImage,
      "sdfgkhajsdkashgdjfbkajshdflajk",
    );
    if (showLessonPlan && lessonPlanQuestionFromState) {
      return (
        <div style={{ position: "fixed", inset: 0 }}>
          {milestoneData?.closingConversation?.bgImage && (
            <img
              src={String(milestoneData.closingConversation.bgImage)}
              alt=""
              style={{
                position: "absolute",
                inset: 0,
                width: "100%",
                height: "100%",
                objectFit: "cover",
                zIndex: 0,
              }}
            />
          )}
          <WebIntegratedLessonPlan
            lessonPlanQuestion={lessonPlanQuestionFromState}
            onStart={() => setShowLessonPlan(false)}
          />
        </div>
      );
    } else {
      return (
        <div className="web-break-root">
          <div
            className="web-break-bg"
            style={{ backgroundImage: bgImage ? `url(${bgImage})` : undefined }}
          />
          <WebSnowfall maxSize={2} />

          {/* Large yeti anchored at bottom-left, above the sheet */}
          {pledgeConversations[0]?.characterImage && (
            <div className="web-break-pledge-yeti-wrap">
              <WebLottiePlayer
                source={
                  pledgeConversations[conversationIndex]?.characterImage ??
                  pledgeConversations[0].characterImage
                }
                className="web-break-pledge-yeti"
                autoPlay
                loop
              />
            </div>
          )}

          {/* Chat bubble floats to the right of the yeti */}
          <div className="web-break-pledge-bubble">
            {renderConversation(pledgeConversations, conversationIndex)}
          </div>

          {/* White bottom sheet – only pledge card + button */}
          <div className="web-break-pledge-sheet">
            <WebPledgeCard
              previousPledge={() => {
                if (conversationIndex > 0) {
                  setConversationIndex((prev) => prev - 1);
                }
              }}
              nextPledge={() => {
                if (conversationIndex < pledgeConversations.length - 1) {
                  setConversationIndex((prev) => prev + 1);
                }
              }}
              conversationIndex={conversationIndex}
              pledgeText={pledgeConversations[conversationIndex]?.content ?? ""}
              maxlength={pledgeConversations.length}
            />

            <WebShadowButton
              title="I pledge!"
              onPress={() => setShowClosingConvo(true)}
            />
          </div>
        </div>
      );
    }
  }

  // ── Standard milestone mode ───────────────────────────────────────────────
  const imageExt = getExtension(milestoneData.image);
  const isLottieImage = imageExt === "json";

  const renderCenterImage = isLottieImage ? (
    <WebLottiePlayer
      source={milestoneData.image ?? ""}
      className="web-break-lottie-center"
      autoPlay
      loop
    />
  ) : (
    <>
      <img src={milestoneData.image} alt="" className="web-break-center-img" />
      {milestoneData.content ? (
        <div className="web-break-content-overlay">
          <p className="web-break-content-text">{milestoneData.content}</p>
        </div>
      ) : null}
    </>
  );

  return (
    <div className="web-break-root">
      <div
        className="web-break-bg"
        style={{ backgroundImage: bgImage ? `url(${bgImage})` : undefined }}
      />
      <WebSnowfall maxSize={2} />

      {/* Step 1 header */}
      {qstnStep === 1 && (
        <header className="web-break-header">
          <button
            type="button"
            className="web-break-header-btn"
            onClick={() => {
              track(TrackingEvents.navigate_to_journey_back);
              // navigate("/web/appJourneyPage")
              navigation.navigate(WEB_SCREENS.AppJourneyPage, {
                replace: true,
              });
            }}
          >
            <WebLottiePlayer
              source={storyAssets.lottie.journeyIcon}
              className="web-break-header-icon"
              autoPlay={false}
              loop={false}
            />
          </button>
          <div className="web-break-header-right">
            <button
              type="button"
              className="web-break-header-btn"
              onClick={() => {}}
            >
              <WebLottiePlayer
                source={storyAssets.lottie.hamburger}
                className="web-break-header-icon"
                autoPlay={false}
                loop={false}
              />
            </button>
          </div>
        </header>
      )}

      {/* Step 1: yeti + chat bubble */}
      {qstnStep === 1 && (
        <>
          <img
            src={yetiPreview}
            alt=""
            className="web-break-step1-yeti-overlay"
          />
          <div className="web-break-step1-chat">
            {renderActivityConversation(STORY_ACTIVITY_CONVERSATION)}
          </div>
        </>
      )}

      {/* Step 0 content */}
      {qstnStep === 0 && (
        <>
          {isMilestoneActivity && milestoneData.content ? (
            <>
              <div className="web-break-activity-yeti-wrap">
                <WebLottiePlayer
                  source={milestoneData.image ?? ""}
                  className="web-break-activity-yeti"
                  autoPlay
                  loop
                />
              </div>
              <div className="web-break-activity-chat">
                {renderActivityConversation(ACTIVITY_CONVERSATION)}
              </div>
            </>
          ) : (
            <div className={!preAudioDone ? "" : "web-break-image-container"}>
              {!preAudioDone ? (
                <>
                  <img
                    src={yetiPreview}
                    alt=""
                    className="web-break-step1-yeti-overlay"
                  />
                  <div className="web-break-step1-chat">
                    {renderActivityConversation(
                      STORY_BREAK_PRE_RENDER_CONVERSATION,
                    )}
                  </div>
                </>
              ) : (
                <>
                  {renderCenterImage}
                  {milestoneData.contentAudio ? (
                    <WebAudioPlayer
                      key="content-audio"
                      uri={milestoneData.contentAudio}
                      contentBaseUrl={contentBaseUrl}
                    />
                  ) : null}
                </>
              )}
            </div>
          )}
        </>
      )}

      {/* Action buttons */}
      {qstnStep === 0 && (
        <div className="web-break-actions">
          {isMilestoneActivity ? (
            <WebShadowButton
              title="Let's go"
              onPress={() => {
                if (milestoneData.takeABreak?.buttons?.[0]) {
                  setAudioUrl(milestoneData.takeABreak.buttons[0].btnAudio);
                  onConfirmBreakAction(
                    milestoneData.takeABreak.buttons[0].btnLinkTo,
                  );
                }
              }}
            />
          ) : (
            <WebShadowButton
              title={!preAudioDone ? "Let's go" : "Oh! I didn't know that."}
              onPress={() => {
                !preAudioDone
                  ? setPreAudioDone(true)
                  : setQstnStep((prev) => prev + 1);
              }}
            />
          )}
        </div>
      )}

      {qstnStep === 1 && (
        <div className="web-break-actions">
          <WebShadowButton
            title="Oooh... what is it?"
            onPress={() => {
              setQstnStep((prev) => prev + 1);
              setShowSelQuestionsModal(true);
            }}
          />
        </div>
      )}

      {showBreakModal && milestoneData.takeABreak && (
        <WebBreakModal
          onClose={() => setShowBreakModal(false)}
          onConfirm={onConfirmBreakAction}
          breakData={milestoneData.takeABreak}
          contentBaseUrl={contentBaseUrl}
        />
      )}

      {!isMilestoneActivity && audioUrl ? (
        <WebAudioPlayer
          uri={audioUrl}
          contentBaseUrl={contentBaseUrl}
          stopAudioPlayer={!!milestoneData?.contentAudio && !preAudioDone}
        />
      ) : null}

      {showSelQuestionsModal && (
        <WebSelQuestionsModal
          questionsData={qstnData}
          onContinue={() => setShowSelQuestionsModal(false)}
          continueToStory={continueToStory}
          storyId={storyId}
          contentBaseUrl={contentBaseUrl}
          onNavigateHome={
            () => routerNavigate(WEB_SCREENS.AppJourneyPage, { replace: true })
            // navigation.navigate(WEB_SCREENS.AppJourneyPage, { replace: true })
          }
        />
      )}

      {showLessonPlan && lessonPlanQuestionFromState && (
        <WebIntegratedLessonPlan
          lessonPlanQuestion={lessonPlanQuestionFromState}
          onStart={() => setShowLessonPlan(false)}
        />
      )}
    </div>
  );
};

export default WebStoryBreakScreen;
